import { UTM_PARAMS } from "~/lib/constants";

const CLEANUP_STORAGE_KEY = "twi.logoutReferralCleanup";
const CLEANUP_PARAM = "auth_cleanup";
const CLEANUP_VALUE = "logout";

let memoryCleanupActive = false;

function getOrigin() {
  return typeof window === "undefined" ? "https://sing.theworshipinitiative.com" : window.location.origin;
}

function toUrl(value: URL | string): URL {
  return value instanceof URL ? new URL(value.toString()) : new URL(value, getOrigin());
}

function toDestination(url: URL): string {
  if (url.origin === getOrigin()) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  return url.toString();
}

function readStoredCleanup(): boolean {
  try {
    return sessionStorage.getItem(CLEANUP_STORAGE_KEY) === CLEANUP_VALUE;
  } catch {
    return false;
  }
}

function writeStoredCleanup() {
  try {
    sessionStorage.setItem(CLEANUP_STORAGE_KEY, CLEANUP_VALUE);
  } catch {
    // The in-memory flag and URL marker still protect the active document.
  }
}

function clearStoredCleanup() {
  try {
    sessionStorage.removeItem(CLEANUP_STORAGE_KEY);
  } catch {
    // The in-memory flag is cleared below regardless of storage availability.
  }
}

function removeTrackedParams(url: URL) {
  for (const param of UTM_PARAMS) {
    url.searchParams.delete(param);
  }
}

function sanitizeNestedRedirect(url: URL) {
  const redirectTo = url.searchParams.get("redirectTo");
  if (!redirectTo) {
    return;
  }

  try {
    const nestedUrl = new URL(redirectTo, getOrigin());
    if (nestedUrl.origin !== getOrigin()) {
      return;
    }

    removeTrackedParams(nestedUrl);
    nestedUrl.searchParams.delete(CLEANUP_PARAM);
    url.searchParams.set("redirectTo", toDestination(nestedUrl));
  } catch {
    // Existing redirect validation owns malformed redirectTo handling.
  }
}

export function beginLogoutReferralCleanup() {
  memoryCleanupActive = true;
  writeStoredCleanup();
}

export function isLogoutReferralCleanupActive(value?: URL | string): boolean {
  if (memoryCleanupActive || readStoredCleanup()) {
    return true;
  }

  if (!value) {
    return false;
  }

  try {
    return toUrl(value).searchParams.has(CLEANUP_PARAM);
  } catch {
    return false;
  }
}

export function sanitizeLogoutReferralUrl(
  value: URL | string,
  options: { preserveMarker?: boolean; sanitizeRedirectTo?: boolean } = {},
): string {
  const url = toUrl(value);
  removeTrackedParams(url);

  if (options.sanitizeRedirectTo) {
    sanitizeNestedRedirect(url);
  }

  if (options.preserveMarker ?? true) {
    url.searchParams.set(CLEANUP_PARAM, CLEANUP_VALUE);
  } else {
    url.searchParams.delete(CLEANUP_PARAM);
  }

  return toDestination(url);
}

export function carryLogoutReferralCleanup(source: URL | string, destination: string): string {
  if (!isLogoutReferralCleanupActive(source)) {
    return destination;
  }

  return sanitizeLogoutReferralUrl(destination);
}

export function completeLogoutReferralCleanup(destination: string): string {
  if (!isLogoutReferralCleanupActive(destination)) {
    return destination;
  }

  const sanitizedDestination = sanitizeLogoutReferralUrl(destination, {
    preserveMarker: false,
    sanitizeRedirectTo: true,
  });

  memoryCleanupActive = false;
  clearStoredCleanup();
  return sanitizedDestination;
}

export function resetLogoutReferralCleanupForTests() {
  memoryCleanupActive = false;
  clearStoredCleanup();
}
